import Requests from "@/services/Requests";

const state = {
  batteries: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  allBatteries: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  createStatus: "unitiliazed", // unitiliazed, loading, success, error
  updateStatus: "unitiliazed", // unitiliazed, loading, success, error
  deleteStatus: "unitiliazed", // unitiliazed, loading, success, error
  batteryById: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  changedLogs: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  batteryPermissions: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
};
const getters = {
  /**
   * Returns a subset of batteries based on the given page number.
   *
   * @param {Object} state - The Vuex state object.
   * @param {number} [page=0] - The page number to filter batteries by. Defaults to 0.
   * @return {Array} An array of batteries filtered by the given page number. If page is 0, returns all batteries.
   */
  getBatteries:
    (state) =>
    (page = 0) =>
      page === 0
        ? state.batteries.data
        : state.batteries.data
            .filter(
              (value, index, self) =>
                index === self.findIndex((t) => t.id === value.id)
            )
            .filter((d) => d.page === page),
  getAllBatteries: (state) => state.allBatteries.data,
  getBatteryStatus: (state) => state.batteries.status,
  getBatteryCount: (state) => state.batteries.count,
  getBatteryPage: (state) => state.batteries.page,
  getBatteryPages: (state) => Math.ceil(state.batteries.count / 10),
  getBatteriesLoading: (state) => state.batteries.status === "loading",
  getBatteriesStatus: (state) => state.batteries.status,
  getBatteryById: (state) => (id) =>
    state.batteryById.data.find((d) => d.id === id) || {},
  getBatteryLoadingById: (state) => state.batteryById.status === "loading",
  getChangedLogsById: (state) => (id) =>
    state.changedLogs.data.find((d) => d.batteryId === id) || { items: [] },
  getChangedLogsLoadingById: (state) => state.changedLogs.status === "loading",
  getBatteryPermissionsById: (state) => (id) =>
    state.batteryPermissions.data.find((d) => d.batteryId === id) || {
      items: [],
    },
  getBatteryPermissionsLoadingById: (state) =>
    state.batteryPermissions.status === "loading",
};
const mutations = {
  /**
   * Updates the status of the battery in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the battery.
   */
  SET_BATTERY_STATUS(state, payload) {
    state.batteries.status = payload;
  },
  /**
   * Sets the batteries and count in the state based on the payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the items and count.
   * @param {Array} payload.items - The array of batteries.
   * @param {number} payload.count - The total count of batteries.
   */
  SET_BATTERIES(state, payload) {
    state.batteries.data = payload.items;
    state.batteries.count = payload.count;
  },
  /**
   * Sets the batteries and count in the state based on the payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the items and count.
   * @param {Array} payload.items - The array of batteries.
   * @param {number} payload.count - The total count of batteries.
   */
  SET_ALL_BATTERIES(state, payload) {
    state.allBatteries.data = payload.items;
    state.allBatteries.count = payload.count;
  },
  /**
   * Updates the status of the update battery in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the battery.
   */
  SET_UPDATE_BATTERY_STATUS(state, payload) {
    state.updateStatus = payload;
  },
  /**
   * Updates an battery object in the state with new information.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The updated battery object.
   * @return {void}
   */
  SET_UPDATE_BATTERY(state, payload) {
    const index = state.batteries.data.findIndex((d) => d.id === payload.id);
    const battery = state.batteries.data[index];
    state.batteries.data[index] = { ...battery, ...payload };
  },
  SET_CREATE_BATTERY_STATUS(state, payload) {
    state.createStatus = payload;
  },
  /**
   * Adds a new battery to the list of batteries in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The battery object to be added.
   * @return {void}
   */
  SET_CREATE_BATTERY(state, payload) {
    state.batteries.data.push(payload);
  },
  /**
   * Updates the status of the delete battery in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the battery.
   */
  SET_DELETE_BATTERY_STATUS(state, payload) {
    state.deleteStatus = payload;
  },
  /**
   * Removes an battery from the state based on the provided payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the battery ID to be deleted.
   * @param {number} payload.id - The ID of the battery to be deleted.
   * @return {void}
   */
  SET_DELETE_BATTERY(state, payload) {
    state.batteries.data = state.batteries.data.filter(
      (d) => d.id !== payload.id
    );
  },
  SET_BATTERY_BY_ID_STATUS(state, payload) {
    state.batteryById.status = payload;
  },
  SET_BATTERY_BY_ID(state, payload) {
    const index = state.batteryById.data.findIndex((d) => d.id === payload.id);
    if (index > -1) state.batteryById.data.splice(index, 1);
    state.batteryById.data.unshift(payload);
  },
  SET_BATTERY_CHANGED_LOGS_BY_ID_STATUS(state, payload) {
    state.changedLogs.status = payload;
  },
  SET_BATTERY_CHANGED_LOGS_BY_ID(state, payload) {
    const index = state.changedLogs.data.findIndex(
      (d) => d.batteryId === payload.batteryId
    );
    if (index > -1) state.changedLogs.data.splice(index, 1);
    state.changedLogs.data.unshift(payload);
  },
  SET_BATTERY_PERMISSIONS_BY_ID_STATUS(state, payload) {
    state.batteryPermissions.status = payload;
  },
  SET_BATTERY_PERMISSIONS_BY_ID(state, payload) {
    const index = state.batteryPermissions.data.findIndex(
      (d) => d.batteryId === payload.batteryId
    );
    if (index > -1) state.batteryPermissions.data.splice(index, 1);
    state.batteryPermissions.data.unshift(payload);
  },
};

const actions = {
  /**
   * Retrieves a list of batteries based on the provided payload.
   *
   * @param {Object} commit - The Vuex commit function.
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the query parameters.
   * @param {Object} payload.query - The query parameters.
   * @param {string} payload.query.paymentMethodId - The ID of the payment method to filter by.
   * @return {Promise<void>}
   */
  async getBatteries({ commit }, payload) {
    commit("SET_BATTERY_STATUS", "loading");
    const response = await Requests.Battery.GetAll(payload);
    if (response.message === "OK") {
      commit("SET_BATTERIES", {
        items: response.data.items.map((item) => ({
          ...item,
          page: payload.query.page || 1,
        })),
        count: response.data.count,
      });
    }
    commit(
      "SET_BATTERY_STATUS",
      response.message === "OK" ? "success" : response.message
    );
    return response.message;
  },
  /**
   * Creates an battery by making a request to the server.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the battery data.
   * @param {string} payload.paymentMethodId - The ID of the payment method.
   * @param {string} payload.batteryRate - The battery rate.
   * @return {Promise<void>} - A promise that resolves when the creation is complete.
   */
  async createBattery({ state, commit }, payload) {
    state.createStatus = "loading";
    try {
      const response = await Requests.Battery.Create(payload);
      state.createStatus = "success";
      if (response.message === "OK")
        commit("SET_CREATE_BATTERY", {
          id: response.data,
          paymentMethodId: payload.paymentMethodId,
          batteryRate: payload.batteryRate,
          isActive: true,
          page: 1,
        });
      else state.createStatus = "error";
    } catch {
      state.createStatus = "error";
    }
  },
  /**
   * Updates an battery by making a request to the server.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the battery data to update.
   * @param {string} payload.paymentMethodId - The ID of the payment method.
   * @param {string} payload.batteryRate - The battery rate.
   * @param {boolean} payload.isActive - Whether the battery is active or not.
   * @return {Promise<void>} - A promise that resolves when the update is complete.
   */
  async updateBattery({ state, commit }, payload) {
    state.updateStatus = "loading";
    try {
      const response = await Requests.Battery.Update(payload, {
        params: { id: payload.id },
      });
      if (response.message === "OK") {
        commit("SET_UPDATE_BATTERY", payload);
        state.updateStatus = "success";
      } else {
        state.updateStatus = "error";
      }
    } catch {
      state.updateStatus = "error";
    }
  },
  /**
   * Removes an battery from the state and updates the delete status.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the battery data.
   * @param {number} payload.id - The ID of the battery to be removed.
   * @returns {Promise<void>} A promise that resolves when the battery is successfully removed.
   */
  removeBattery({ state }, { id }) {
    state.deleteStatus = "loading";

    return Requests.Battery.Delete({ params: { id } })
      .then((response) => {
        state.deleteStatus = response.message === "OK" ? "success" : "error";
        return response;
      })
      .catch(() => {
        state.deleteStatus = "error";
        return { message: "ERROR" };
      });
  },
  /**
   * Retrieves an battery by their ID and updates the battery by ID status in the Vuex store.
   *
   * @param {Object} commit - The Vuex commit function.
   * @param {Object} id - The ID of the battery to retrieve.
   * @return {Promise} A promise that resolves when the battery is successfully retrieved and the status is updated.
   */
  getBatteryById({ commit, state }, { id, force = false }) {
    // check if battery already exists in the state;
    if (state.batteryById.data.find((battery) => battery?.id === id) && !force)
      return {
        message: "OK",
        data: state.batteryById.data.find((battery) => battery.id === id),
      };
    commit("SET_BATTERY_BY_ID_STATUS", "loading");
    return Requests.Battery.GetById({ params: { id } })
      .then((response) => {
        if (response.message === "OK") {
          commit("SET_BATTERY_BY_ID", response.data);
          commit("SET_BATTERY_BY_ID_STATUS", "success");
        } else {
          commit("SET_BATTERY_BY_ID_STATUS", "error");
        }
        return response;
      })
      .catch((error) => {
        commit("SET_BATTERY_BY_ID_STATUS", "error");
        return error.response;
      });
  },
  getBatteryChangedLogsById({ commit, state }, { batteryId, force = false }) {
    // check if battery already exists in the state;
    console.log(batteryId, "batteryId");

    if (
      state.changedLogs.data.find(
        (battery) => battery.batteryId === batteryId
      ) &&
      !force
    )
      return state.changedLogs.data.find(
        (battery) => battery.batteryId === batteryId
      );
    commit("SET_BATTERY_CHANGED_LOGS_BY_ID_STATUS", "loading");
    return Requests.Battery.GetBatteryLogs({ query: { id: batteryId } })
      .then((response) => {
        commit("SET_BATTERY_CHANGED_LOGS_BY_ID", {
          batteryId,
          ...response.data,
        });
        commit("SET_BATTERY_CHANGED_LOGS_BY_ID_STATUS", "success");
        return response.data;
      })
      .catch(() => {
        commit("SET_BATTERY_CHANGED_LOGS_BY_ID_STATUS", "error");
        return {};
      });
  },
  getBatteryPermissionsById({ commit, state }, { batteryId, force = false }) {
    // check if battery already exists in the state;

    if (
      state.batteryPermissions.data.find(
        (battery) => battery.batteryId === batteryId
      ) &&
      !force
    )
      return state.batteryPermissions.data.find(
        (battery) => battery.batteryId === batteryId
      );
    commit("SET_BATTERY_PERMISSIONS_BY_ID_STATUS", "loading");
    return Requests.BatteryPermissions.GetBatteryPermissionUsers({
      query: { batteryId: batteryId },
    })
      .then((response) => {
        commit("SET_BATTERY_PERMISSIONS_BY_ID", {
          batteryId,
          ...response.data,
        });
        commit("SET_BATTERY_PERMISSIONS_BY_ID_STATUS", "success");
        return response.data;
      })
      .catch(() => {
        commit("SET_BATTERY_PERMISSIONS_BY_ID_STATUS", "error");
        return {};
      });
  },
};

export default { state, getters, mutations, actions };
